import { Injectable, computed, inject, signal } from '@angular/core';
import { Observable, catchError, map, of, timeout } from 'rxjs';
import { jwtDecode } from 'jwt-decode';
import { AuthService } from '../../../auth/services/auth.service';
import { Actor, UsuarioEntity } from '../core/models/asistencia.model';
import { Capacidad, tieneCapacidad } from '../core/models/permisos';

interface RolJwtMJ {
  id?: number;
  rol_id?: number;
  rol?: string;
  name?: string;
}

interface UsuarioJwtMJ {
  id?: number;
  first_name?: string;
  middle_name?: string;
  last_name?: string;
  second_surname?: string;
  name?: string;
  email?: string;
  phone?: string;
  identification_number?: string | number;
  roles?: Array<RolJwtMJ | string | number>;
}

/**
 * Identidad del panel administrativo en Medellín Joven.
 * Resuelve el usuario autenticado y su rol (Coordinación o Mentor) desde `AuthService`.
 */
@Injectable({ providedIn: 'root' })
export class AsistenciaSesionService {
  private readonly authService = inject(AuthService);
  private readonly catalogo = signal<UsuarioEntity[]>([]);
  private readonly mjUser = signal<UsuarioJwtMJ | null>(this.obtenerUsuarioLocalInicial());
  private ultimoRefrescoRolesMs = 0;

  constructor() {
    this.authService.user$.subscribe((u) => {
      const resuelto = (u as UsuarioJwtMJ) ?? this.obtenerUsuarioLocalInicial();
      if (resuelto) {
        this.mjUser.set(resuelto);
      }
    });
  }

  private obtenerUsuarioLocalInicial(): UsuarioJwtMJ | null {
    const enMemoria = (this.authService.getUser() as UsuarioJwtMJ) ?? null;
    if (enMemoria && Array.isArray(enMemoria.roles)) {
      return enMemoria;
    }
    const token = this.authService.getToken();
    if (!token) return enMemoria;
    try {
      return jwtDecode<UsuarioJwtMJ>(token);
    } catch {
      return enMemoria;
    }
  }

  get usuarios(): UsuarioEntity[] {
    return this.catalogo();
  }

  /**
   * Determina estrictamente si el usuario de Medellín Joven posee un rol autorizado
   * para acceder al módulo administrativo de Asistencia MML.
   * Devuelve `null` si el usuario no tiene ninguno de los roles del módulo.
   */
  resolverRolAutorizadoDesdeJwt(user: UsuarioJwtMJ | null): 'COORDINADOR_MML' | 'MENTOR_MML' | null {
    if (!user || !Array.isArray(user.roles) || user.roles.length === 0) {
      return null;
    }

    let esCoord = false;
    let esMentor = false;

    for (const r of user.roles) {
      const rolId =
        typeof r === 'number'
          ? r
          : Number((r as RolJwtMJ)?.rol_id ?? (r as RolJwtMJ)?.id ?? NaN);
      const texto = (
        typeof r === 'string'
          ? r
          : String((r as RolJwtMJ)?.rol ?? (r as RolJwtMJ)?.name ?? '')
      )
        .trim()
        .toLowerCase();

      if (
        rolId === 2 ||
        texto === 'administrador' ||
        texto === 'superadmin' ||
        texto === 'coordinador asistencia mml' ||
        texto === 'coordinador_mml'
      ) {
        esCoord = true;
      } else if (
        texto === 'mentor asistencia mml' ||
        texto === 'mentor_mml'
      ) {
        esMentor = true;
      }
    }

    if (esCoord) return 'COORDINADOR_MML';
    if (esMentor) return 'MENTOR_MML';
    return null;
  }

  /**
   * Indica si el usuario tiene otros roles administrativos en Medellín Joven
   * distintos al rol base de ciudadano (`Usuario`) o `Estudiante Asistencia MML`.
   */
  tieneOtroRolAdministrativoMj(user?: UsuarioJwtMJ | null): boolean {
    const u = user ?? this.mjUser();
    if (!u || !Array.isArray(u.roles)) return false;
    return u.roles.some((r) => {
      const rolId =
        typeof r === 'number'
          ? r
          : Number((r as RolJwtMJ)?.rol_id ?? (r as RolJwtMJ)?.id ?? NaN);
      const texto = (
        typeof r === 'string'
          ? r
          : String((r as RolJwtMJ)?.rol ?? (r as RolJwtMJ)?.name ?? '')
      )
        .trim()
        .toLowerCase();
      return (
        rolId !== 1 &&
        texto !== '' &&
        texto !== 'usuario' &&
        texto !== 'estudiante asistencia mml'
      );
    });
  }

  /**
   * Valida la sesión y sincroniza los roles activos del usuario contra PostgreSQL (`refreshToken`).
   * Si el dispositivo está sin conexión a internet (`!navigator.onLine`) o con señal débil,
   * resuelve el rol localmente desde el JWT almacenado sin bloquear la navegación.
   */
  verificarAccesoEnServidor(): Observable<'COORDINADOR_MML' | 'MENTOR_MML' | null> {
    const enLinea = typeof navigator !== 'undefined' ? navigator.onLine : true;
    const usuarioLocal = this.mjUser() ?? this.obtenerUsuarioLocalInicial();

    if (!usuarioLocal) {
      this.mjUser.set(null);
      return of(null);
    }

    this.mjUser.set(usuarioLocal);
    const rolLocal = this.resolverRolAutorizadoDesdeJwt(usuarioLocal);

    if (!enLinea) {
      return of(rolLocal);
    }

    const ahora = Date.now();
    const ttlMs = rolLocal !== null ? 10 * 60_000 : 60_000;
    if (ahora - this.ultimoRefrescoRolesMs < ttlMs) {
      return of(rolLocal);
    }

    const email = usuarioLocal?.email?.trim();
    if (!email) {
      this.ultimoRefrescoRolesMs = ahora;
      return of(rolLocal);
    }

    this.ultimoRefrescoRolesMs = ahora;
    return this.authService.refreshToken(email).pipe(
      timeout({ first: 4_000 }),
      map((resp: any) => {
        this.ultimoRefrescoRolesMs = Date.now();
        const dataUser = resp?.data?.data ?? resp?.data ?? null;
        if (resp?.success && dataUser) {
          if (dataUser.token) {
            this.authService.accessToken = dataUser.token;
          }
          const rolNuevo = this.resolverRolAutorizadoDesdeJwt(dataUser as UsuarioJwtMJ);
          if (rolNuevo !== rolLocal) {
            this.authService.setUser(dataUser);
          }
          this.mjUser.set(dataUser as UsuarioJwtMJ);
          return rolNuevo;
        }
        return rolLocal;
      }),
      catchError(() => {
        this.ultimoRefrescoRolesMs = Date.now();
        return of(rolLocal);
      })
    );
  }

  readonly rolAutorizado = computed<'COORDINADOR_MML' | 'MENTOR_MML' | null>(() =>
    this.resolverRolAutorizadoDesdeJwt(this.mjUser())
  );

  readonly tieneAccesoModulo = computed<boolean>(() => this.rolAutorizado() !== null);

  readonly usuario = computed<UsuarioEntity>(() => {
    const lista = this.catalogo();
    const uJwt = this.mjUser();
    const rolResuelto = this.rolAutorizado() ?? 'MENTOR_MML';

    if (uJwt && uJwt.id) {
      const mjId = Number(uJwt.id);
      const correo = (uJwt.email ?? '').trim().toLowerCase();
      const doc = uJwt.identification_number ? String(uJwt.identification_number).trim() : '';

      const activos = lista.filter((u) => !u.eliminado && u.activo !== false);
      const encontrado =
        activos.find((u) => u.mjUserId === mjId) ??
        (correo ? activos.find((u) => u.correo.trim().toLowerCase() === correo) : undefined) ??
        (doc ? activos.find((u) => u.numeroDocumento === doc) : undefined);

      if (encontrado) {
        return {
          ...encontrado,
          rol: rolResuelto === 'COORDINADOR_MML' ? 'COORDINADOR_MML' : encontrado.rol
        };
      }

      const nombreCompleto =
        [uJwt.first_name, uJwt.middle_name, uJwt.name, uJwt.last_name, uJwt.second_surname]
          .filter(Boolean)
          .join(' ')
          .replace(/\s+/g, ' ')
          .trim() ||
        uJwt.email ||
        'Usuario Medellín Joven';

      return {
        id: `usr-mj-${mjId}`,
        codigo: rolResuelto === 'MENTOR_MML' ? `M-${mjId}` : `C-${mjId}`,
        nombre: nombreCompleto,
        correo: uJwt.email ?? '',
        celular: uJwt.phone ?? undefined,
        numeroDocumento: doc || undefined,
        rol: rolResuelto,
        mjUserId: mjId,
        activo: true
      };
    }

    return (
      lista[0] ?? {
        id: 'usr-sesion-mj',
        codigo: 'C001',
        nombre: 'Coordinación Medellín Music Lab',
        correo: '',
        rol: rolResuelto,
        mjUserId: 0,
        activo: false
      }
    );
  });

  readonly rol = computed(() => this.usuario().rol);
  readonly esCoordinacion = computed(() => this.rolAutorizado() === 'COORDINADOR_MML');
  readonly esMentor = computed(() => this.rolAutorizado() === 'MENTOR_MML');

  readonly actor = computed<Actor>(() => {
    const u = this.usuario();
    return {
      id: u.id,
      nombre: u.nombre,
      rol: u.rol,
      canal: 'ADMIN'
    };
  });

  sincronizarUsuarios(usuarios: UsuarioEntity[]): void {
    this.catalogo.set(usuarios);
  }

  puede(capacidad: Capacidad): boolean {
    const rol = this.rolAutorizado();
    if (!rol) return false;
    return tieneCapacidad(rol, capacidad);
  }
}
